import { createContext, useContext, type ReactNode } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import type { PublicConfig } from '@cca/config';
import { apiRequest } from './api.js';

/**
 * Runtime configuration, fetched from the worker rather than baked into the
 * bundle. This keeps the product name, environment and flag state changeable
 * without a rebuild, and guarantees the browser only ever holds the explicit
 * public allow-list.
 */

const RuntimeConfigContext = createContext<PublicConfig | null>(null);

export function useRuntimeConfig(): PublicConfig {
  const value = useContext(RuntimeConfigContext);
  if (!value) throw new Error('useRuntimeConfig must be used inside RuntimeConfigProvider');
  return value;
}

export const runtimeConfigQuery = {
  queryKey: ['runtime-config'] as const,
  queryFn: () => apiRequest<PublicConfig>('/api/config'),
  // Configuration changes rarely; refetching on every mount is noise.
  staleTime: 5 * 60 * 1000,
};

export function RuntimeConfigProvider({ children }: { readonly children: ReactNode }) {
  const { data } = useSuspenseQuery(runtimeConfigQuery);
  return <RuntimeConfigContext.Provider value={data}>{children}</RuntimeConfigContext.Provider>;
}
